import type { DurationSpec, BriefInput, Destination, Kind } from './content'

/**
 * Impor CSV untuk Create — TANPA format baku. User memetakan kolomnya sendiri
 * ke field brief; fungsi di sini murni (tanpa DOM/fetch) supaya bisa diuji.
 */

export interface Csv {
  headers: string[]
  rows: string[][]
}

/** Tebak pemisah dari baris pertama: koma, titik koma (Excel lokal ID/EU), atau tab. */
export function detectDelimiter(text: string): string {
  const first = text.split(/\r?\n/, 1)[0] ?? ''
  const hitung = (d: string) => first.split(d).length - 1
  return [',', ';', '\t'].sort((a, b) => hitung(b) - hitung(a))[0]!
}

/**
 * Urai CSV (RFC 4180): kutip ganda, kutip di-escape "", baris baru di dalam
 * sel ber-kutip, BOM UTF-8. Baris kosong dibuang.
 */
export function parseCsv(text: string, delimiter = detectDelimiter(text)): Csv {
  const s = text.replace(/^﻿/, '')
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let kutip = false
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!
    if (kutip) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"'
          i++
        } else kutip = false
      } else cell += ch
    } else if (ch === '"' && cell === '') kutip = true
    else if (ch === delimiter) {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else cell += ch
  }
  if (cell !== '' || row.length) {
    row.push(cell)
    rows.push(row)
  }
  const bersih = rows.filter((r) => r.some((c) => c.trim() !== ''))
  const headers = (bersih.shift() ?? []).map((h, i) => h.trim() || `Column ${i + 1}`)
  return { headers, rows: bersih }
}

export const TARGET_FIELDS = [
  { key: 'url', label: 'Link', hint: 'The article or page to turn into content' },
  { key: 'title', label: 'Title', hint: 'Used when there is no link, or to override it' },
  { key: 'text', label: 'Text', hint: 'Your own material or notes to write from' },
  { key: 'notes', label: 'Instructions', hint: 'What to focus on, tone, call to action…' },
  { key: 'imageUrl', label: 'Image link', hint: 'A photo to use in the design' },
  { key: 'account', label: 'Project', hint: 'Project name or the @username of its account' },
  { key: 'kind', label: 'Content type', hint: 'poster, carousel, video, or both' },
  { key: 'style', label: 'Style', hint: 'Carousel or video style, e.g. listicle' },
  { key: 'duration', label: 'Video length', hint: 'auto, 1-5, 5-10, or minutes like 12' },
  { key: 'destination', label: 'After production', hint: 'bank, queue, or publish' },
] as const

export type TargetKey = (typeof TARGET_FIELDS)[number]['key']

export type FieldMap = { source: 'none' } | { source: 'column'; column: string } | { source: 'fixed'; value: string }

export interface Mapping {
  fields: Record<TargetKey, FieldMap>
  /** Kolom yang tidak dipetakan ikut dikirim ke AI sebagai konteks. */
  extraToAi: boolean
}

export function emptyMapping(headers: string[]): Mapping {
  const fields = Object.fromEntries(TARGET_FIELDS.map((f) => [f.key, { source: 'none' }])) as Record<TargetKey, FieldMap>
  // Tebakan awal dari nama kolom, supaya CSV yang rapi tidak perlu dipetakan manual.
  const tebak: Record<TargetKey, RegExp> = {
    url: /^(url|link|tautan|source ?url|article)$/i,
    title: /^(title|judul|headline)$/i,
    text: /^(text|body|content|isi|description|deskripsi)$/i,
    notes: /^(notes?|instructions?|catatan|brief)$/i,
    imageUrl: /^(image|image ?url|photo|gambar|foto)$/i,
    account: /^(project|proyek|account|akun|username|handle)$/i,
    kind: /^(type|kind|format|jenis)$/i,
    style: /^(style|gaya)$/i,
    destination: /^(destination|tujuan|publish|after)$/i,
    duration: /^(duration|length|durasi|minutes?|menit)$/i,
  }
  for (const f of TARGET_FIELDS) {
    const col = headers.find((h) => tebak[f.key].test(h.trim()))
    if (col) fields[f.key] = { source: 'column', column: col }
  }
  return { fields, extraToAi: true }
}

export interface AccountRef {
  id: string
  name: string
  username: string | null
}

export function matchAccount(raw: string, accounts: AccountRef[]): AccountRef | null {
  const v = raw.trim().replace(/^@/, '').toLowerCase()
  if (!v) return null
  return accounts.find((a) => a.id === raw.trim() || a.name.toLowerCase() === v || (a.username ?? '').toLowerCase() === v) ?? null
}

export function normalizeKinds(raw: string): Kind[] | null {
  const v = raw.trim().toLowerCase()
  if (!v) return null
  const out = new Set<Kind>()
  for (const part of v.split(/[\s,;/+&]+|\band\b|\bdan\b/)) {
    if (/^(poster|image|single|picture|gambar|foto|pin)$/.test(part)) out.add('poster')
    else if (/^(carousel|slides?|slideshow|listicle|karosel)$/.test(part)) out.add('carousel')
    else if (/^(video|reels?|clip|short|shorts|vid)$/.test(part)) out.add('video')
    else if (/^(both|keduanya|all|semua)$/.test(part)) {
      out.add('poster')
      out.add('carousel')
    }
  }
  return out.size ? [...out] : null
}

/**
 * Durasi video dari sel CSV: "auto", "1-5", "5–10 mins", "12", "12 min",
 * "90s", "1:30". Angka tunggal = menit Custom.
 */
export function normalizeDuration(raw: string): DurationSpec | null {
  const v = raw.trim().toLowerCase().replace(/–/g, '-')
  if (!v) return null
  if (/^(auto|otomatis)$/.test(v)) return { mode: 'auto' }
  if (/^1\s*-\s*5\b/.test(v)) return { mode: '1-5' }
  if (/^5\s*-\s*10\b/.test(v)) return { mode: '5-10' }
  const mmss = /^(\d{1,2}):(\d{2})$/.exec(v)
  if (mmss) return { mode: 'custom', minutes: Math.round((Number(mmss[1]) + Number(mmss[2]) / 60) * 4) / 4 }
  const n = /^(\d+(?:[.,]\d+)?)\s*(s|sec|secs|seconds?|detik|m|min|mins|minutes?|menit)?$/.exec(v)
  if (!n) return null
  const angka = Number(n[1]!.replace(',', '.'))
  const menit = n[2] && /^(s|sec|detik)/.test(n[2]) ? angka / 60 : angka
  return menit > 0 && menit <= 60 ? { mode: 'custom', minutes: Math.max(0.25, Math.round(menit * 4) / 4) } : null
}

export function normalizeDestination(raw: string): Destination | null {
  const v = raw.trim().toLowerCase()
  if (!v) return null
  if (/^(bank|save|draft|simpan)/.test(v)) return 'bank'
  if (/^(queue|schedule|scheduled|antre|jadwal)/.test(v)) return 'queue'
  if (/^(publish|post( now)?|now|terbit|langsung)/.test(v)) return 'publish'
  return null
}

export interface MappedRow {
  index: number
  brief: BriefInput | null
  error: string | null
  /** Ringkasan untuk pratinjau. */
  summary: { url: string; title: string; account: string; kinds: string; destination: string }
}

/** Terapkan pemetaan ke setiap baris. Baris yang tidak sah diberi alasan, tidak membatalkan yang lain. */
export function applyMapping(csv: Csv, m: Mapping, accounts: AccountRef[]): MappedRow[] {
  const idx = new Map(csv.headers.map((h, i) => [h, i]))
  const terpakai = new Set(Object.values(m.fields).flatMap((f) => (f.source === 'column' ? [f.column] : [])))
  return csv.rows.map((r, index) => {
    const get = (k: TargetKey): string => {
      const f = m.fields[k]
      if (f.source === 'fixed') return f.value
      if (f.source === 'column') return (r[idx.get(f.column) ?? -1] ?? '').trim()
      return ''
    }
    const url = get('url')
    const title = get('title')
    const text = get('text')
    const accountRaw = get('account')
    const acc = matchAccount(accountRaw, accounts)
    // Sel kosong = bawaan (poster); hanya nilai terisi yang tak dikenal yang ditolak.
    const kindRaw = get('kind')
    const kinds = kindRaw ? normalizeKinds(kindRaw) : (['poster'] as Kind[])
    const destRaw = get('destination')
    const destination = destRaw ? normalizeDestination(destRaw) : null
    const durRaw = get('duration')
    const duration = durRaw ? normalizeDuration(durRaw) : null
    let notes = get('notes')
    if (m.extraToAi) {
      const extra = csv.headers.filter((h) => !terpakai.has(h)).map((h) => [h, (r[idx.get(h)!] ?? '').trim()] as const).filter(([, v]) => v)
      if (extra.length) notes = [notes, ...extra.map(([h, v]) => `${h}: ${v}`)].filter(Boolean).join('\n')
    }

    let error: string | null = null
    if (url && !/^https?:\/\//i.test(url)) error = 'The link must start with http:// or https://'
    else if (!url && !text && !title) error = 'No link, title, or text'
    else if (!acc) error = accountRaw ? `No project called "${accountRaw}"` : 'No project chosen'
    else if (!kinds) error = `Unknown content type "${kindRaw}"`
    else if (destRaw && !destination) error = `Unknown destination "${destRaw}"`
    else if (durRaw && !duration && kinds?.includes('video')) error = `Unknown video length "${durRaw}"`

    const imageUrl = get('imageUrl')
    return {
      index: index + 1,
      error,
      brief: error
        ? null
        : {
            urls: url ? [url] : [],
            title,
            text,
            notes,
            imageUrl: /^https?:\/\//i.test(imageUrl) ? imageUrl : null,
            workspaceIds: [acc!.id],
            kinds: kinds!,
            style: get('style') || null,
            destination,
            ...(kinds?.includes('video') && duration ? { duration } : {}),
          },
      summary: {
        url,
        title,
        account: acc?.name ?? accountRaw,
        kinds: (kinds ?? []).join(' + '),
        destination: destination ?? (destRaw || 'account default'),
      },
    }
  })
}
